import React, { ReactElement } from 'react';
import styled, { ColorsTypes, FontsWeights } from 'styled-components';

import {
  space,
  border,
  flexbox,
  layout,
  position,
  borders,
  SpaceProps,
  BorderProps,
  FlexboxProps,
  LayoutProps,
  PositionProps,
  BordersProps,
} from 'styled-system';

import { Flex } from 'components';
import Icons, { IconsTypes } from 'components/icons';

type ButtonSizes = 'small' | 'medium' | 'large';
type CursorTypes = 'default' | 'pointer' | 'inherit';

type MessageIconAllProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  SpaceProps &
  BorderProps &
  FlexboxProps &
  LayoutProps &
  PositionProps &
  BordersProps;

export interface MessageIconProps extends MessageIconAllProps {
  icon: IconsTypes;
  size?: ButtonSizes;
  iconWidth?: { m?: number; t?: number; d?: number } | number;
  label?: string;
  color?: ColorsTypes;
  colorHover?: ColorsTypes;
  colorClick?: ColorsTypes;
  fontWeight?: FontsWeights;

  bg?: ColorsTypes | 'transparent';
  bgHover?: ColorsTypes | 'transparent';
  bgClick?: ColorsTypes | 'transparent';

  labelColor?: ColorsTypes;
  labelColorHover?: ColorsTypes;
  labelColorClick?: ColorsTypes;
  labelSize?: number;

  tooltip?: string;
  tooltipBg?: ColorsTypes;
  tooltipColor?: ColorsTypes;
  cursor?: CursorTypes;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  buttonSize: ButtonSizes;
  fontWeight: FontsWeights;
  iconWidth?: { m?: number; t?: number; d?: number } | number;
  iconPercentage: number;

  color: ColorsTypes;
  colorHover: ColorsTypes;
  colorClick: ColorsTypes;
  labelSize?: number;

  labelColor: ColorsTypes;
  labelColorHover: ColorsTypes;
  labelColorClick: ColorsTypes;

  bg: ColorsTypes | 'transparent';
  bgHover: ColorsTypes | 'transparent';
  bgClick: ColorsTypes | 'transparent';

  cursor?: CursorTypes;
}

const Tooltip = styled('div')<{ bg: ColorsTypes; color: ColorsTypes }>`
  display: none;
  position: absolute;
  background: ${({ theme, bg }) => theme.colors[bg]};
  color: ${({ theme, color }) => theme.colors[color]};
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 4px;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  z-index: 50;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid ${({ theme, bg }) => theme.colors[bg]};
    left: 50%;
    transform: translateX(-50%);
  }
`;

const Label = styled('div')`
  text-transform: uppercase;
  transition: 0.2s;
  white-space: nowrap;
  text-align: center;
  margin-top: 4px;
`;

const Icon = styled(Icons)`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: 0.2s;
`;

const IconWrapper = styled.div`
  display: inline-block;
  position: relative;
  justify-content: center;
`;

const Button = styled('button')<ButtonProps>`
  display: flex;
  align-items: center;
  border: 0;
  padding: 0;
  cursor: ${({ disabled, cursor }) => (disabled ? 'inherit' : cursor ? cursor : 'default')};
  color: ${({ theme, color }) => theme.colors[color]};
  background: ${({ theme, bg }) => (bg === 'transparent' ? 'transparent' : theme.colors[bg])};
  transition: 0.2s;
  outline: none;
  position: relative;
  ${space}
  ${border}
  ${flexbox}
  ${layout}
  ${position}
  ${borders}

  ${IconWrapper} {
    ${({ buttonSize, iconWidth }) => {
      if (iconWidth) {
        return `
          width: ${iconWidth}px;
          height: ${iconWidth}px;
        `;
      }

      switch (buttonSize) {
        case 'small':
          return `
            width: 14px;
            height: 14px;
          `;

        case 'large':
          return `
            width: 24px;
            height: 24px;
          `;

        default:
        case 'medium':
          return `
            width: 18px;
            height: 18px;
          `;
      }
    }}
  }

  ${Icon} {
    color: ${({ theme, color }) => theme.colors[color]};

    ${({ buttonSize, iconWidth, iconPercentage, theme: { mediaQueries } }) => {
      if (iconWidth) {
        if (typeof iconWidth === 'object') {
          let response = '';

          if (iconWidth.m) {
            response = `
              ${response}

              ${mediaQueries.mobile} {
                width: ${Math.ceil(iconWidth.m + (iconWidth.m / 100) * iconPercentage)}px;
                height: ${Math.ceil(iconWidth.m + (iconWidth.m / 100) * iconPercentage)}px;
              }
            `;
          }

          if (iconWidth.t) {
            response = `
              ${response}

              ${mediaQueries.tablet} {
                width: ${Math.ceil(iconWidth.t + (iconWidth.t / 100) * iconPercentage)}px;
                height: ${Math.ceil(iconWidth.t + (iconWidth.t / 100) * iconPercentage)}px;
              }
            `;
          }

          if (iconWidth.d) {
            response = `
              ${response}

              ${mediaQueries.desktop} {
                width: ${Math.ceil(iconWidth.d + (iconWidth.d / 100) * iconPercentage)}px;
                height: ${Math.ceil(iconWidth.d + (iconWidth.d / 100) * iconPercentage)}px;
              }
            `;
          }

          return `
              ${response}
            `;
        } else {
          return `
            width: ${Math.ceil(iconWidth + (iconWidth / 100) * iconPercentage)}px;
            height: ${Math.ceil(iconWidth + (iconWidth / 100) * iconPercentage)}px;
          `;
        }
      }

      switch (buttonSize) {
        case 'small':
          return `
            width: 19px;
            height: 19px;
          `;

        case 'large':
          return `
            width: 28px;
            height: 28px;
          `;

        default:
        case 'medium':
          return `
            width: 21px;
            height: 21px;
          `;
      }
    }}
  }

  ${Label} {
    color: ${({ theme, labelColor }) => theme.colors[labelColor]};
    font-weight: ${({ theme, fontWeight }) => theme.fontWeights[fontWeight]};

    ${({ buttonSize, labelSize, theme }) => {
      if (labelSize) {
        return `
            font-size: ${labelSize}px;
          `;
      } else {
        switch (buttonSize) {
          case 'small':
            return `
            font-size: ${theme.fontSizes[0]};
          `;

          default:
          case 'medium':
            return `
              font-size: ${theme.fontSizes[1]};
            `;

          case 'large':
            return `
            font-size: ${theme.fontSizes[2]};
          `;
        }
      }
    }}
  }

  &:hover {
    background: ${({ theme, bgHover }) => (bgHover === 'transparent' ? 'transparent' : theme.colors[bgHover])};

    ${Icon} {
      color: ${({ theme, colorHover }) => theme.colors[colorHover]};
    }

    ${Label} {
      color: ${({ theme, labelColorHover }) => theme.colors[labelColorHover]};
    }

    ${Tooltip} {
      display: block;
    }
  }

  &:active {
    background: ${({ theme, bgClick }) => (bgClick === 'transparent' ? 'transparent' : theme.colors[bgClick])};

    ${Icon} {
      color: ${({ theme, colorClick }) => theme.colors[colorClick]};
    }

    ${Label} {
      color: ${({ theme, labelColorClick }) => theme.colors[labelColorClick]};
    }
  }
`;

const MessageIcon = ({
  label,
  size,
  iconWidth,
  color,
  colorHover,
  colorClick,
  labelColor,
  labelColorHover,
  labelColorClick,
  labelSize,
  bg,
  bgHover,
  bgClick,
  icon,
  fontWeight,
  tooltip,
  tooltipBg = 'dark_400',
  tooltipColor = 'gray_100',
  type = 'button',
  cursor = 'pointer',
  children,
  ...buttonProps
}: MessageIconProps): ReactElement => {
  const buttonBg = bg || 'primary_400';
  const buttonBgHover = bgHover || (buttonBg === 'primary_400' ? 'primary_200' : buttonBg);
  const buttonBgClick = bgClick || (buttonBgHover === 'primary_200' ? 'primary_600' : buttonBgHover);

  const buttonColor = color || 'white';

  const buttonColorHover =
    colorHover || (buttonColor === 'white' ? 'white' : buttonColor) || (buttonColor === 'primary_400' ? 'primary_200' : buttonColor);

  const buttonColorClick =
    colorClick || (buttonColorHover === 'white' ? 'white' : buttonColorHover) || (buttonColorHover === 'primary_200' ? 'primary_600' : buttonColor);

  const buttonLabelColor = labelColor || buttonColor;
  const buttonLabelColorHover = labelColorHover || buttonColorHover;
  const buttonLabelColorClick = labelColorClick || buttonColorClick;

  const buttonSize = size || 'medium';

  const buttonFontWeight = fontWeight || 'bold';

  let iconPercentage: number = 16.66;

  return (
    <Button
      color={buttonColor}
      colorHover={buttonColorHover}
      colorClick={buttonColorClick}
      buttonSize={buttonSize}
      iconWidth={iconWidth}
      iconPercentage={iconPercentage}
      bg={buttonBg}
      bgHover={buttonBgHover}
      bgClick={buttonBgClick}
      labelColor={buttonLabelColor}
      labelColorHover={buttonLabelColorHover}
      labelColorClick={buttonLabelColorClick}
      fontWeight={buttonFontWeight}
      cursor={cursor}
      type={type}
      labelSize={labelSize}
      {...buttonProps}
    >
      {!!tooltip && (
        <Tooltip bg={tooltipBg} color={tooltipColor}>
          {tooltip}
        </Tooltip>
      )}

      <Flex alignItems="center" justifyContent="center" flexWrap="nowrap" position="relative" flexDirection="column">
        <IconWrapper>
          <Icon icon={icon} />
        </IconWrapper>

        {Boolean(label) && <Label>{label}</Label>}
      </Flex>
    </Button>
  );
};
export default MessageIcon;
